import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTenant } from '@emerald-works-nova/auth'
import { useDispatch, useSelector } from 'react-redux'
import { useLoaderData, useLocation, useParams } from 'react-router'
import { useGroup } from '../../hooks'
import { contentSlice } from '../../reducers'
import { CONTENT_QUERY_STRING } from '../../consts'
import { ALL_AUTHENTICATED } from '../../services/cognito/userGroups'
import Auth from '@aws-amplify/auth'

const sessionIsActive = async () => {
  try {
    await Auth.currentSession()
    return true
  } catch (error) {
    return false
  }
}

const useContentLoad = ({ onLoadContent = (content) => {} }) => {
  const loaderData = useLoaderData()
  const dispatch = useDispatch()
  const tenant = useTenant()
  // eslint-disable-next-line
  const { locationId, '*': staticUrl } = useParams()
  const { isInGroup } = useGroup()
  const allLanguages = useSelector(contentSlice.selectors.selectAllLanguages)
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [error, setError] = useState(null)
  const isUrlAdjusted = useSelector(contentSlice.selectors.selectIsUrlAdjusted)
  const { search } = useLocation()
  const translationQuery = useMemo(() => (new URLSearchParams(search)).get(CONTENT_QUERY_STRING.TRANSLATION), [search])
  const langRef = useRef(translationQuery || 'en')

  // eslint-disable-next-line
  const fetchContent = useCallback(async () => {
    const languageIsValid = translationQuery && translationQuery !== 'en' && allLanguages.some(({ value }) => value === translationQuery)
    const shouldTranslate = languageIsValid && (isInGroup(ALL_AUTHENTICATED) || await sessionIsActive())
    const params = {
      queryStringParameters: {
        tenantId: tenant.pk,
        locationId,
        ...shouldTranslate && { language: translationQuery }
      }
    }

    const { API } = await import('@aws-amplify/api')

    const payload = await API.get('event_bus_token_api_name', '/tenant-content', params)
    return payload
  }, [locationId, tenant.pk, isInGroup, allLanguages, translationQuery])

  const loadContentFromLoader = useCallback(async () => {
    return new Promise((resolve, reject) => {
      if (!loaderData.contentPromise) {
        return reject(new Error('Content promise not found'))
      }

      loaderData.contentPromise.then((data) => {
        if (data.loadClient) {
          if (window.DD_RUM && typeof window.DD_RUM.addAction === 'function') {
            window.DD_RUM.addAction('loadArticleFromClient', { locationId })
          } else {
            console.log('addAction is not available')
          }
          return fetchContent().then(resolve).catch(reject)
        } else {
          if (window.DD_RUM && typeof window.DD_RUM.addAction === 'function') {
            window.DD_RUM.addAction('loadArticleFromCdn', { locationId })
          } else {
            console.log('addAction is not available')
          }
          return resolve(data)
        }
      }).catch(err => reject(err))
    })
  }, [loaderData, fetchContent, locationId])

  const loadContent = useCallback(async () => {
    setIsLoadingContent(true)
    let contentResponse
    try {
      const loaderResponse = await loadContentFromLoader()
      contentResponse = loaderResponse
    } catch (err) {
      console.error('Error loading content from loader', err)
      try {
        contentResponse = await fetchContent()
      } catch (err) {
        console.error('Error loading content from API', err)
        setError(err)
      }
    } finally {
      dispatch(contentSlice.actions.setContent(contentResponse))
      if (translationQuery) {
        dispatch(contentSlice.actions.setLanguage(translationQuery || 'en'))
      }
      dispatch(contentSlice.actions.setIsTranslating(false))
      onLoadContent(contentResponse.content)
      setIsLoadingContent(false)
    }
  }, [fetchContent, loadContentFromLoader, dispatch, onLoadContent, translationQuery])

  useEffect(() => {
    console.log('tRanslation query', translationQuery)
    const langHasChanged = translationQuery !== langRef.current
    if (!isUrlAdjusted || langHasChanged) {
      langHasChanged && (langRef.current = translationQuery)
      loadContent()
    }
  }, [loadContent, isUrlAdjusted, translationQuery])

  return {
    isLoadingContent,
    error
  }
}

export { useContentLoad }
